import { useMemo, useState } from 'react';
import { filterCategories, rankingVideos } from '@/mocks/homeData';
import type { RankingVideo } from '@/pages/home/types';

const videos = rankingVideos as RankingVideo[];

function rankBadgeClass(rank: number) {
  if (rank === 1) return 'bg-primary-500 text-background-50';
  if (rank === 2) return 'bg-secondary-500 text-foreground-950';
  if (rank === 3) return 'bg-accent-500 text-background-50';
  return 'bg-background-100 text-foreground-600 border border-background-200';
}

export default function RankingSection() {
  const [active, setActive] = useState('全部');

  const list = useMemo(
    () => (active === '全部' ? videos : videos.filter((v) => v.category === active)),
    [active],
  );

  return (
    <section id="ranking" className="w-full bg-background-50 px-4 py-14 md:px-8 md:py-20">
      <div className="mx-auto w-full max-w-6xl">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 text-sm text-primary-600">
              <i className="ri-trophy-line"></i>
              全站实时热度
            </span>
            <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-4xl">热门视频榜单 · 今日 TOP 12</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              依据播放量、互动与近期增速综合排序。点击下方分区标签可只看某一类内容，标签为「全部」时展示完整榜单。
            </p>
          </div>
          <div className="text-sm text-foreground-500">
            当前共 <span className="font-heading text-xl text-primary-600">{list.length}</span> 条内容
          </div>
        </div>

        <div className="mt-6 inline-flex max-w-full flex-wrap gap-1 rounded-full border border-background-200 bg-background-100 p-1">
          {filterCategories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setActive(cat)}
              className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                active === cat
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <ol className="mt-8 flex flex-col gap-3">
          {list.map((video) => (
            <li
              key={video.title}
              className="group flex flex-col gap-4 rounded-lg border border-background-200/80 bg-background-100/60 p-4 transition-colors hover:border-primary-200 md:flex-row md:items-stretch md:p-5"
            >
              <div className="flex items-center gap-4 md:w-auto">
                <span
                  className={`w-10 h-10 shrink-0 flex items-center justify-center rounded-lg font-heading text-lg ${rankBadgeClass(
                    video.rank,
                  )}`}
                >
                  {video.rank}
                </span>
                <div className="relative h-24 w-40 shrink-0 overflow-hidden rounded-md sm:h-28 sm:w-48">
                  <img
                    src={video.cover}
                    alt={`${video.title} 视频封面`}
                    title={`${video.title} - 西瓜视频热门视频`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute bottom-1.5 right-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[11px] text-background-50">
                    {video.duration}
                  </span>
                </div>
              </div>

              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-sm text-foreground-950 md:text-base">{video.title}</h3>
                  <span
                    className={`shrink-0 inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs ${
                      video.trend === 'up'
                        ? 'bg-accent-100 text-accent-800'
                        : 'bg-background-200 text-foreground-600'
                    }`}
                  >
                    <i className={video.trend === 'up' ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>
                    {video.change}
                  </span>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600 md:text-sm">{video.summary}</p>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500 md:text-sm">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {video.author}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-play-circle-line"></i>
                    {video.plays}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-heart-3-line"></i>
                    {video.likes}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-800">
                    {video.category}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}